<script setup lang="ts">
/* eslint-disable max-lines -- the smart-input search view legitimately exceeds the
   default cap. */
import { computed, onMounted, ref } from 'vue';
import { useDisplay } from 'vuetify';

import { pluralize } from '@/utils/strings/pluralize.util';
import { formatCount } from '@/utils/numbers/format-count.util';
import { isProRecentlyActive } from '@/core/sourcing/utils/dates';

import ProSearchResults from '@/components/Search/ProSearchResults/ProSearchResults.vue';
import AddToJobModal from '@/components/Search/AddProToJobModal.vue';
import SmartSearchHeader from '@/components/Search/SmartInput/SmartSearchHeader.vue';
import SmartFilters from '@/components/Search/SmartInput/SmartFilters.vue';
import SmartFiltersPanel from '@/components/Search/SmartInput/SmartFiltersPanel.vue';
import StagedSearchLoader from '@/components/Search/SmartInput/StagedSearchLoader.vue';
import RecentSearchesEmpty from '@/components/Search/SmartInput/RecentSearchesEmpty.vue';

import SpinnerLoader from '@/components/Shared/Loaders/SpinnerLoader.vue';

import SelectedCandidateService from '@/core/sourcing/selected-candidate/selected-candidate.service';
import { SearchService } from '@/core/sourcing/search/search.service';
import { SearchService as SharedSearchService } from '@/core/shared/search/search.service';
import { SearchTab } from '@/core/sourcing/search/types/search-tab.enum';
import type { SearchMode } from '@/core/sourcing/search/types/search-mode.type';
import { detectTypedMode } from '@/core/sourcing/search/utils/detect-mode.util';
import SubscriptionService from '@/core/shared/subscription/subscription.service';
import type { SelectedGatedUserProfile } from '@/core/sourcing/messaging/types/selected-gated-user-profile.type';

import {
  ProUnlockResultStatus,
  ProUnlockSource,
} from '@/core/sourcing/pro-unlock/types/pro-unlock-source.type';

import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import { MessagingService } from '@/core/sourcing/messaging/messaging.service';
import ProUnlockService from '@/core/sourcing/pro-unlock/pro-unlock.service';
import { UNLOCK_LIMIT_REACHED_TOAST } from '@/core/sourcing/pro-unlock/consts/unlock-limit-reached-toast.const';
import { isUnlockLimitError } from '@/core/sourcing/pro-unlock/utils/is-unlock-limit-error.util';
import ProjectService from '@/core/shared/project/project.service';
import { PageSource } from '@/core/shared/tracking/tracking.type';
import { useRoute, useRouter } from 'vue-router';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import { EmployerFeatureGatingService } from '@/core/shared/employer-feature-gating/employer-feature-gating.service';
import { FeatureName } from '@factoryfixinc/ats-interfaces';
import { MarketingReportType } from '@/components/LaborMarket/labor-market-report-options';

const display = useDisplay();
const route = useRoute();
const router = useRouter();

const searchService = new SearchService();
const selectedCandidateService = new SelectedCandidateService();
const messagingService = new MessagingService();
const proUnlockService = new ProUnlockService();
const projectService = new ProjectService();
const sharedSearchService = new SharedSearchService();
const dialogService = new DialogService();
const featureGatingService = new EmployerFeatureGatingService();
const subscriptionService = SubscriptionService.instance;

const isUnlockingUserProfiles = ref(false);
// Keep action attribution tied to the result set that opened the modal, even if the unlock
// refresh commits a newer response before the candidate action is submitted.
const addToJobSearchSessionId = ref<string | undefined>();

const isLoading = computed(() => sharedSearchService.isSearchingCandidates);
// Why the current loading cycle started: 'query' = a new user-initiated search, which may
// play the staged NL loader; 'navigation' = page / items-per-page changes, which always keep
// the simple spinner (ONE-6609). Set by beginSearchFlight and deliberately never reset in
// `finally`: it is only read while isLoading is true, and a settle-time reset could clobber
// a newer overlapping flight's reason. Defaults to 'navigation' because isSearchingCandidates
// is store state that outlives this component — a remount mid-flight can't know the reason,
// and an unknown reason must fall back to the plain spinner, not replay the staged loader.
const loadingReason = ref<'query' | 'navigation'>('navigation');
// Two monotonic generations guard against out-of-order search flights — including the silent
// unlock/messaging refreshes, which never set isSearchingCandidates. clearGeneration bumps on
// every clear affordance; searchGeneration bumps when a user-initiated search starts. Every
// flight snapshots both before its request and passes flightIsCurrent as the store-commit
// gate (shouldCommitResults): a flight superseded by a clear or a newer search never writes
// its results or inferred filters, so a stale response can neither blank nor replace the
// newer search's results. The post-await checks below reuse the same predicate to skip a
// stale flight's toast / results-URL write.
const clearGeneration = ref(0);
const searchGeneration = ref(0);

// A user-initiated search claims the next search generation, so any flight that started
// earlier is superseded by it. A silent refresh does not claim one — a search starting after
// it supersedes the refresh instead.
function beginSearchFlight(reason: 'query' | 'navigation'): {
  clearGen: number;
  searchGen: number;
} {
  loadingReason.value = reason;
  searchGeneration.value += 1;
  return { clearGen: clearGeneration.value, searchGen: searchGeneration.value };
}

function beginRefreshFlight(): { clearGen: number; searchGen: number } {
  return { clearGen: clearGeneration.value, searchGen: searchGeneration.value };
}

// True while the flight is still the latest action: no clear and no newer search has happened
// since it started. Drives both the store-commit gate and the post-await post-work skip.
function flightIsCurrent(flight: { clearGen: number; searchGen: number }): boolean {
  return clearGeneration.value === flight.clearGen && searchGeneration.value === flight.searchGen;
}
// Natural-language searches get the staged loader; keyword/Boolean and job searches keep
// the existing simple spinner (ONE-6171). An active job is never NL, regardless of mode.
const isNlSearch = computed(
  () => !sharedSearchService.activeJob && sharedSearchService.searchMode === 'natural',
);
const openMessagingDialog = ref(false);
const projects = computed(() => projectService.projects);
const selectedPros = computed(() => messagingService.selectedProUserProfiles);
const hasDoNotContactRecipient = computed(() =>
  selectedPros.value.some((pro) => pro.doNotContact === true),
);

const canGenerateMarketingReports = computed(
  () =>
    featureGatingService.hasAccess(FeatureName.MARKETABLE_CANDIDATE_REPORTS) &&
    subscriptionService.isEmployerSubscribed,
);
const marketingReportsCount = computed(() => messagingService.selectedProUserProfiles.length);

async function openMarketingReports() {
  try {
    isUnlockingUserProfiles.value = true;

    // Unlock any locked selected candidates first, the same way "Unlock & Add to Job" does.
    const userProfilesToUnlock = messagingService.selectedUserProfilesToUnlock.map(
      (userProfile: SelectedGatedUserProfile) => userProfile.id,
    );

    if (userProfilesToUnlock.length) {
      const shouldDisplayTrialModal = subscriptionService.shouldDisplayTrialModal({
        unlockCount: userProfilesToUnlock.length,
      });
      if (shouldDisplayTrialModal) {
        messagingService.setIsTrialUpgradeModalVisible({
          visible: true,
          source: ProUnlockSource.BULK_MESSAGING,
        });
        return;
      }

      const flight = beginRefreshFlight();
      const searchSessionId = searchService.searchSessionId;
      const result = await proUnlockService.unlockProUserProfiles(
        userProfilesToUnlock,
        ProUnlockSource.BULK_MESSAGING,
        searchSessionId,
      );

      // Refresh the results so the cards reflect the newly unlocked state. The refresh
      // is silent (no loader); the commit gate drops its response if a clear or newer search
      // landed while it was in flight, so it can't blank or replace the current results. The
      // unlock itself stands and the report flow continues regardless.
      await sharedSearchService.searchCandidatesWithAdditionalInformation({
        shouldCommitResults: () => flightIsCurrent(flight),
      });
      messagingService.markSelectedUserProfilesAsUnlocked(userProfilesToUnlock);

      if (result?.status === ProUnlockResultStatus.PARTIAL_SUCCESS) {
        SnackbarService.cautionAlt(result.message);
      }
    }

    dialogService.openLaborMarketReports({
      defaultType: MarketingReportType.JOB_MATCH,
      candidates: messagingService.selectedProUserProfiles.map((profile) => ({
        userProfileId: profile.id,
        name: profile.fullName,
        ...(profile.subtitle ? { subtitle: profile.subtitle } : {}),
      })),
    });
  } catch (error) {
    ErrorService.captureException(error);
    if (isUnlockLimitError(error)) {
      SnackbarService.criticalAlt(UNLOCK_LIMIT_REACHED_TOAST);
    } else {
      SnackbarService.critical('Could not unlock user profiles, please try again later.');
    }
  } finally {
    isUnlockingUserProfiles.value = false;
  }
}

const smartHeaderRef = ref<InstanceType<typeof SmartSearchHeader> | null>(null);
// The header's live resolved mode (job > forced chip > auto-detect), pushed up via
// update:mode. Tracks the active chip before any search so keyword/Boolean can reveal
// the filter chrome on detection. Seed it from the current search state so a deep link
// with a job or keyword/Boolean query renders the right layout on the first frame,
// rather than flashing the transition once the header mounts and emits.
const liveMode = ref<SearchMode>(
  sharedSearchService.activeJob ? 'job' : detectTypedMode(sharedSearchService.keyword ?? ''),
);
// Bind the header straight to the shared keyword so the input and the executed search
// never diverge — the legacy keyword field binds the same way. Clearing the input
// clears the keyword filter.
const smartQuery = computed<string>({
  get: () => sharedSearchService.keyword ?? '',
  set: (value) => {
    sharedSearchService.keyword = value || undefined;
  },
});

onMounted(async () => {
  // Need to fetch projects for the 'add to job modal' to work
  if (projects.value.length === 0) {
    fetchProjects();
  }

  // Whether the URL itself carries a search (deep/shared link), captured before
  // resolveUrlQuery folds any persisted filters into the query.
  const urlHasSearch = sharedSearchService.urlQueryHasFilters(
    route.query as unknown as Record<string, string | null>,
  );

  const newUrlQuery = await sharedSearchService.resolveUrlQuery(route.query);
  await router.replace({ query: newUrlQuery });

  if (sharedSearchService.alreadyExecutedASearch) {
    applyCandidateListScroll();
    return;
  }

  // Smart-input experience: land on the recent-searches + examples empty state instead of
  // auto-running a blank search on a fresh visit. Still auto-run when the URL carries a
  // search (deep/shared link).
  if (!urlHasSearch) {
    return;
  }

  // A deep/shared link runs like a typed search: resolve its mode so the request routes
  // through the smart wrapper (NL extraction, session recording) (ONE-6178).
  if (!sharedSearchService.searchMode && sharedSearchService.keyword) {
    sharedSearchService.searchMode = detectTypedMode(sharedSearchService.keyword);
  }

  try {
    sharedSearchService.isSearchingCandidates = true;
    const flight = beginSearchFlight('query');

    await sharedSearchService.searchCandidatesWithAdditionalInformation({
      shouldCommitResults: () => flightIsCurrent(flight),
    });
  } catch (error) {
    ErrorService.captureException(error);
  } finally {
    sharedSearchService.isSearchingCandidates = false;
    applyCandidateListScroll();
  }
});

const isDesktop = computed(() => display.mdAndUp.value);

// The smart shell fills the area below DefaultLayout's app bar. On mobile (smAndDown)
// that v-app-bar offsets v-main, so subtract its height; desktop has no app bar. dvh
// keeps it correct as mobile browser chrome shows/hides.
const smartShellStyle = computed(() => ({
  height: isDesktop.value ? '100dvh' : 'calc(100dvh - 64px)',
}));
const showBulkMessageButton = computed(() => {
  return (
    messagingService.selectedProUserProfiles.length > 0 &&
    messagingService.selectedPPCUserProfiles.length > 0
  );
});
const selectedUserProfilesToUnlockCount = computed(() => {
  return messagingService.selectedUserProfilesToUnlock.length;
});
const bulkMessageRecipientsCount = computed(() => {
  return messagingService.selectedPPCUserProfiles.length;
});

const bulkUnlockButtonMessage = computed(() => {
  const isSameCount = selectedUserProfilesToUnlockCount.value === bulkMessageRecipientsCount.value;

  if (isSameCount) {
    return `Unlock & Add to Job (${selectedUserProfilesToUnlockCount.value})`;
  }

  const unlockText = selectedUserProfilesToUnlockCount.value
    ? `Unlock (${selectedUserProfilesToUnlockCount.value})`
    : '';
  const separator = unlockText ? ' & ' : '';
  const recipientsText = ` Add to Job (${bulkMessageRecipientsCount.value})`;

  return `${unlockText}${separator}${recipientsText}`;
});

// Mirrors bulkUnlockButtonMessage: the unlock count is the locked selected candidates,
// the report count is the full selection. All locked → "Unlock & Request Job Match Report (N)";
// some locked → "Unlock (X) & Request Job Match Report (N)"; none locked → "Request Job Match Report (N)".
const marketingReportsButtonMessage = computed(() => {
  const unlockCount = selectedUserProfilesToUnlockCount.value;
  const totalCount = marketingReportsCount.value;

  if (unlockCount === totalCount) {
    return `Unlock & Request Job Match Report (${totalCount})`;
  }

  const unlockText = unlockCount ? `Unlock (${unlockCount}) & ` : '';
  return `${unlockText}Request Job Match Report (${totalCount})`;
});

async function handleResultsPageChange(page: number) {
  try {
    sharedSearchService.isSearchingCandidates = true;
    const flight = beginSearchFlight('navigation');
    await sharedSearchService.searchCandidatesWithAdditionalInformation({
      page,
      resetTab: false,
      shouldCommitResults: () => flightIsCurrent(flight),
    });

    if (!flightIsCurrent(flight)) {
      return;
    }

    const newUrlQuery = await sharedSearchService.resolveUrlQuery(route.query, {
      localStateIsPriority: true,
    });

    await router.replace({ query: newUrlQuery });
  } catch (error) {
    ErrorService.captureException(error);
  } finally {
    sharedSearchService.isSearchingCandidates = false;
    applyCandidateListScroll(0);
  }
}

async function handleResultsItemsPerPageChange(itemsPerPage: number) {
  try {
    sharedSearchService.isSearchingCandidates = true;
    const flight = beginSearchFlight('navigation');

    await sharedSearchService.searchCandidatesWithAdditionalInformation({
      itemsPerPage,
      resetTab: false,
      shouldCommitResults: () => flightIsCurrent(flight),
    });

    if (!flightIsCurrent(flight)) {
      return;
    }

    const newUrlQuery = await sharedSearchService.resolveUrlQuery(route.query, {
      localStateIsPriority: true,
    });

    await router.replace({ query: newUrlQuery });
  } catch (error) {
    ErrorService.captureException(error);
  } finally {
    sharedSearchService.isSearchingCandidates = false;
    applyCandidateListScroll(0);
  }
}

// Shared smart-input search execution. Runs whatever mode handleSmartSearch resolved
// into the store — keyword/Boolean, natural-language, or job — and syncs the result URL.
async function executeSmartSearch() {
  if (sharedSearchService.isSearchingCandidates) {
    return;
  }

  // A search needs something to search: a typed query or an active job — a blank query
  // would search the entire pool and pollute recent searches with blank sessions.
  // Results from a cleared query are dropped so the view returns to the empty state
  // instead of showing results the current input no longer describes.
  if (!sharedSearchService.activeJob && !sharedSearchService.keyword?.trim()) {
    searchService.resetResults();
    return;
  }

  try {
    sharedSearchService.isSearchingCandidates = true;
    const flight = beginSearchFlight('query');
    selectedCandidateService.clearSelectedCandidate();
    messagingService.removeSelectedProUserProfiles();

    const { fellBackToKeyword } =
      await sharedSearchService.searchCandidatesWithAdditionalInformation({
        page: 1,
        shouldCommitResults: () => flightIsCurrent(flight),
      });

    // A clear or a newer search landed while this request was in flight: skip its post-work
    // (no toast, no results-URL write). The commit gate keeps this response's results out of
    // the store, so the newer state stands.
    if (!flightIsCurrent(flight)) {
      return;
    }

    // The backend used keyword results after an extraction failure or zero-hit hybrid search.
    if (fellBackToKeyword) {
      SnackbarService.cautionAlt('Showing keyword results instead.');
    }

    const urlQuery = await sharedSearchService.resolveUrlQuery(route.query, {
      localStateIsPriority: true,
    });
    await router.replace({ query: urlQuery });
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Could not run your search. Please try again.');
  } finally {
    sharedSearchService.isSearchingCandidates = false;
    applyCandidateListScroll(0);
  }
}

function handleSmartSearch({ query, mode }: { query: string; mode: SearchMode }) {
  sharedSearchService.keyword = query;
  // Persist the mode the header resolved (chip override or auto-detect) so it drives the
  // backend wrapper and the loader, and survives recents reruns and pagination.
  sharedSearchService.searchMode = mode;
  return executeSmartSearch();
}

// The sidebar's Apply is the same action as pressing Search, just placed where the filters
// are: delegate to the header's submit so the payload, its guards, session capture, loader
// gating and URL sync are all the header's rather than a second implementation that can drift.
// One consequence worth knowing: query text typed but not submitted goes along, exactly as it
// would if the recruiter had reached for Search instead.
function handleApplyFilters() {
  smartHeaderRef.value?.submit();
}

// Selecting a job is an explicit search action, like pressing Search — unlike sidebar
// filter changes, which only stage. Await the full job load (description, title,
// coordinates) so the request is fully built before running; selectActiveJob
// already surfaces failures, so any outcome but 'loaded' skips the auto-run.
async function handleSelectJob(jobId: number) {
  const outcome = await sharedSearchService.selectActiveJob(jobId);
  if (outcome !== 'loaded') {
    return;
  }
  // Byte-identical to the header's Search-button payload in job mode (the input is
  // cleared and disabled, effectiveMode is 'job'), so session capture, loader gating,
  // and URL sync behave exactly like a manual submit.
  return handleSmartSearch({ query: '', mode: 'job' });
}

// The deep-link filter keys (urlQueryHasFilters) plus the pagination pair — exactly
// what getUrlQueryFromSelectedFilters writes. Unrelated params survive the strip.
const SEARCH_URL_KEYS = [
  'keyword',
  'location',
  'distance',
  'jobTitle',
  'lastActive',
  'page',
  'itemsPerPage',
  'payRateMin',
  'payRateMax',
  'shifts',
  'willingToRelocate',
  'openToTravel',
  'languages',
  'responsiveness',
  'recentlyOpenToWork',
  'inferred',
  'includeUnknown',
] as const;

// An explicit clear affordance (the input's X, or clearing the active job) returns the
// view to the recent-searches empty state: results reset (showEmptyState flips back,
// re-mounting RecentSearchesEmpty so it refetches recents), inferred sidebar values from
// the cleared query drop (user-set values stay), and the search params are stripped from
// the URL so a reload doesn't deep-link the cleared search back. Deleting text
// character-by-character is NOT a clear — only these affordances reset.
async function handleClearSearch() {
  // Bump before any await so every response still in flight — loader-backed searches
  // and the silent unlock/messaging refreshes alike — lands on a stale generation and
  // gets dropped by its flight's own check.
  clearGeneration.value += 1;

  searchService.resetResults();
  addToJobSearchSessionId.value = undefined;
  sharedSearchService.applyInferredFilters([]);

  const query = { ...route.query };
  for (const key of SEARCH_URL_KEYS) {
    delete query[key];
  }
  await router.replace({ query });
}

// Clearing the job (banner X, the input's X in job mode, or switching to a typed chip)
// is clearing the search. The reset lives here — NOT inside clearActiveJob — because
// applyRecentSearch calls clearActiveJob mid-rerun and must not wipe results.
function handleClearJob() {
  sharedSearchService.clearActiveJob();
  return handleClearSearch();
}

async function openMessaging({ source }: { source: ProUnlockSource }) {
  if (hasDoNotContactRecipient.value) {
    addToJobEntryPoint.value = undefined;
    addToJobSearchSessionId.value = undefined;
    return;
  }
  try {
    isUnlockingUserProfiles.value = true;
    // Capture before unlock: its silent refresh can commit a newer response before the modal
    // sends a message or starts AI outreach.
    const searchSessionId = searchService.searchSessionId;
    addToJobSearchSessionId.value = searchSessionId;
    const userProfilesToUnlock = messagingService.selectedUserProfilesToUnlock.map(
      (userProfile: SelectedGatedUserProfile) => userProfile.id,
    );

    if (!userProfilesToUnlock?.length) {
      openMessagingDialog.value = true;
      return;
    }

    const subscriptionService = new SubscriptionService();
    const shouldDisplayTrialModal = subscriptionService.shouldDisplayTrialModal({
      unlockCount: userProfilesToUnlock.length,
    });
    if (shouldDisplayTrialModal) {
      messagingService.setIsTrialUpgradeModalVisible({
        visible: true,
        source,
      });
      // Doesn't open Add to Job — nothing will call onAddProToJobModalClose to clear a
      // resume entry point set right before this call, so clear it here too.
      addToJobEntryPoint.value = undefined;
      addToJobSearchSessionId.value = undefined;
      return;
    }

    const flight = beginRefreshFlight();
    const result = await proUnlockService.unlockProUserProfiles(
      userProfilesToUnlock,
      source,
      searchSessionId,
    );

    // After unlock, refresh the search results. The refresh is silent (no loader); the commit
    // gate drops its response if a clear or newer search landed while it was in flight, so it
    // can't blank or replace the current results. The unlock stands and the dialog still opens.
    await sharedSearchService.searchCandidatesWithAdditionalInformation({
      shouldCommitResults: () => flightIsCurrent(flight),
    });

    messagingService.markSelectedUserProfilesAsUnlocked(userProfilesToUnlock);
    // Will be shown only if there are some user profiles that were not unlocked:
    // this is a partial success otherwise we wont show any message
    if (result?.status === ProUnlockResultStatus.PARTIAL_SUCCESS) {
      SnackbarService.cautionAlt(result.message);
    }
    if (hasDoNotContactRecipient.value) {
      addToJobEntryPoint.value = undefined;
      addToJobSearchSessionId.value = undefined;
      SnackbarService.cautionAlt(
        'Add to job is unavailable because a selected candidate is marked Do Not Contact.',
      );
      return;
    }
    openMessagingDialog.value = true;

    const event =
      messagingService.selectedProUserProfiles?.length > 1
        ? TrackingActionName.BULK_ACTION_START
        : TrackingActionName.MESSAGE_START;

    TrackingService.trackAction(event, {
      num_unlocks: userProfilesToUnlock?.length,
      num_pros: messagingService.selectedProUserProfiles?.length,
      num_recently_active:
        messagingService.selectedProUserProfiles?.filter((pro) =>
          isProRecentlyActive(pro.lastActiveTs),
        )?.length ?? 0,
    });
  } catch (error) {
    // Doesn't open Add to Job — nothing will call onAddProToJobModalClose to clear a
    // resume entry point set right before this call, so clear it here too.
    addToJobEntryPoint.value = undefined;
    addToJobSearchSessionId.value = undefined;
    ErrorService.captureException(error);
    if (isUnlockLimitError(error)) {
      SnackbarService.criticalAlt(UNLOCK_LIMIT_REACHED_TOAST);
    } else {
      SnackbarService.critical('Could not unlock user profiles, please try again later.');
    }
  } finally {
    isUnlockingUserProfiles.value = false;
  }
}

async function fetchProjects() {
  try {
    await projectService.searchProjects({
      skipOnSameSearch: true,
      skipTracking: false,
      pageSource: PageSource.Search,
    });
    await projectService.searchAvailableProjects();
  } catch (error) {
    ErrorService.captureException(error);
  }
}

const resultsHeaderText = computed(() => {
  return `${formatCount(searchService.serverItems)} ${pluralize(
    searchService.serverItems,
    'candidate',
  )} found`;
});

const showEmptyState = computed(() => searchService.serverItems === undefined);

// Hide the filter chrome on the bare empty state so "Find your next hire" centers
// full-width. Show it once a search has results (every mode), or early — while still
// on the empty state — the moment the keyword/Boolean chip activates (auto or forced).
const showFilters = computed(() => !showEmptyState.value || liveMode.value === 'keyword_boolean');

async function onAddProToJobModalSubmit() {
  if (addToJobEntryPoint.value === 'resume') {
    TrackingService.trackAction(TrackingActionName.RESUME_ADD_TO_JOB_COMPLETED, {
      had_active_job: addToJobInitialJobId.value !== undefined,
    });
  }
  const selectedCandidateService = new SelectedCandidateService();
  await selectedCandidateService.refreshCandidate();
  messagingService.removeSelectedProUserProfiles();
}

function onAddProToJobModalClose(params: { messageSent: boolean }) {
  // If the message was sent, refresh the results to reflect it. This refresh is silent
  // (fire-and-forget, no loader); its commit gate drops the response if a clear or a newer
  // search landed while it was in flight, so it can't resurrect a cleared search or replace
  // a newer one.
  if (params.messageSent) {
    const flight = beginRefreshFlight();
    sharedSearchService
      .searchCandidatesWithAdditionalInformation({
        shouldCommitResults: () => flightIsCurrent(flight),
      })
      .catch((error) => {
        ErrorService.captureException(error);
      });
  }
  openMessagingDialog.value = false;
  addToJobSearchSessionId.value = undefined;
  // A resume-originated session is always exactly the one PPC-tagged candidate added in
  // ProSearchResults' addToJobFromResume — removeNonPPCProsFromSelectedUserProfiles()
  // deliberately keeps PPC profiles (so a canceled card-level add stays selected for a
  // still-visible checkbox), but the resume flow has no such visible affordance. Left
  // selected, a canceled resume candidate could get silently swept into a later,
  // unrelated bulk Add to Job submission.
  if (addToJobEntryPoint.value === 'resume') {
    messagingService.removeSelectedProUserProfiles();
  } else {
    messagingService.removeNonPPCProsFromSelectedUserProfiles();
  }
  addToJobEntryPoint.value = undefined;
}

// Set right before opening Add to Job from the resume viewer so the modal can preselect
// the active job and completion tracking can attribute back to the resume. Cleared on
// close so a later card/bulk open never inherits resume context.
const addToJobEntryPoint = ref<'resume' | undefined>(undefined);

function openAddToJobFromResume() {
  addToJobEntryPoint.value = 'resume';
  openMessaging({ source: ProUnlockSource.PPC });
}

const addToJobInitialJobId = computed(() =>
  addToJobEntryPoint.value === 'resume' ? sharedSearchService.activeJob?.id : undefined,
);

function onAddProToJobModalRemovePro(id: number) {
  messagingService.removeProFromSelectedUserProfiles(id);
}

function onCandidateListScroll(event: Event) {
  if (!event.target) {
    return;
  }

  const candidateList = event.target as HTMLDivElement;
  sharedSearchService.candidateListScrollTop = candidateList.scrollTop;
}

function applyCandidateListScroll(scrollTop?: number) {
  requestAnimationFrame(() => {
    const candidateList = document.getElementsByClassName('candidates-list')?.[0];

    if (candidateList) {
      candidateList.scrollTop = scrollTop ?? sharedSearchService.candidateListScrollTop;
    }
  });
}
</script>

<template>
  <!-- The shell is a flex column so the smart header + collapsible filters stay pinned
       while the candidate list scrolls. The results/empty markup sits below the smart
       header/filters chrome. -->
  <div
    class="search-shell search-shell--smart"
    :class="{
      'search-shell--rail': isDesktop,
      'search-shell--rail-collapsed': isDesktop && !showFilters,
    }"
    :style="smartShellStyle"
  >
    <SmartSearchHeader
      ref="smartHeaderRef"
      v-model="smartQuery"
      :is-searching="isLoading"
      :active-job="sharedSearchService.activeJobBanner"
      @search="handleSmartSearch"
      @select-job="handleSelectJob"
      @clear="handleClearSearch"
      @clear-job="handleClearJob"
      @update:mode="(mode) => (liveMode = mode)"
    />

    <!-- Filter chrome is hidden on the bare empty state (gated on showFilters) so it
         doesn't push the empty state off-center; it eases in via the fade transition
         below when results arrive or the keyword/Boolean chip activates. Desktop keeps
         the prototype's slim sidebar; mobile collapses it to a panel below the search.
         Filter values live in the shared store, so swapping the components across the
         breakpoint preserves them, and the header's Search button picks them up — filter
         changes never run a search on their own. The v-if/v-else-if keeps a single
         element under Transition (its required single child) so the enter/leave
         classes attach. -->
    <Transition name="filter-chrome" mode="out-in">
      <aside v-if="showFilters && isDesktop" class="smart-sidebar">
        <SmartFilters @apply="handleApplyFilters" />
      </aside>
      <SmartFiltersPanel v-else-if="showFilters" @apply="handleApplyFilters" />
    </Transition>

    <!-- An in-flight search outranks the empty state, so the loading block below shows
         from submit (staged loader for NL) instead of only after the response populates
         serverItems. -->
    <RecentSearchesEmpty
      v-if="showEmptyState && !isLoading"
      @rerun="(recent) => sharedSearchService.applyRecentSearch(recent).then(executeSmartSearch)"
      @use-example="(text) => handleSmartSearch({ query: text, mode: detectTypedMode(text) })"
    />

    <template v-else>
      <div v-show="!isLoading" class="results-main">
        <div v-if="searchService.serverItems" class="results-header">
          <!-- Candidates Count -->
          <span class="text-sm leading-[18px]">{{ resultsHeaderText }}</span>

          <div class="flex items-center gap-3">
            <!-- Request Job Match Report -->
            <button
              v-if="canGenerateMarketingReports && marketingReportsCount > 0"
              data-test="generate-marketing-reports"
              class="max-h-[32px] items-center rounded-[6px] border border-shade-900 bg-tint-0 px-3 py-2 text-sm font-semibold leading-4 text-shade-900 disabled:opacity-75"
              :disabled="isUnlockingUserProfiles"
              @click="openMarketingReports"
            >
              <template v-if="isUnlockingUserProfiles">
                <SpinnerLoader class="mx-4 h-[18px] w-[18px]" />
              </template>
              <template v-else>
                {{ marketingReportsButtonMessage }}
              </template>
            </button>

            <!-- Bulk Messaging Button -->
            <button
              v-if="showBulkMessageButton"
              data-test="bulk-add-to-job"
              class="max-h-[32px] items-center rounded-[6px] bg-highlight-600 px-3 py-2 leading-4 disabled:opacity-75"
              :disabled="isUnlockingUserProfiles || hasDoNotContactRecipient"
              @click="openMessaging({ source: ProUnlockSource.BULK_MESSAGING })"
            >
              <template v-if="isUnlockingUserProfiles">
                <SpinnerLoader class="mx-4 h-[18px] w-[18px]" dark></SpinnerLoader>
              </template>
              <template v-else>
                {{ bulkUnlockButtonMessage }}
              </template>
            </button>
          </div>
        </div>

        <!-- Tab Toggle -->
        <div v-if="searchService.serverItems" class="tab-toggle-container relative z-10">
          <div class="tab-toggle">
            <button
              class="tab-toggle-button"
              :class="{
                'tab-toggle-button--active': searchService.activeTab === SearchTab.MATCHES,
              }"
              @click="searchService.activeTab = SearchTab.MATCHES"
            >
              Matches
            </button>
            <button
              class="tab-toggle-button"
              :class="{
                'tab-toggle-button--active': searchService.activeTab === SearchTab.SKIPPED,
              }"
              @click="searchService.activeTab = SearchTab.SKIPPED"
            >
              Skipped
            </button>
          </div>
        </div>

        <!-- Candidates List -->
        <div class="candidates-list" @scroll="onCandidateListScroll">
          <ProSearchResults
            :is-loading="sharedSearchService.isSearchingCandidates"
            :has-active-job="!!sharedSearchService.activeJob"
            @change:page="handleResultsPageChange"
            @change:items-per-page="handleResultsItemsPerPageChange"
            @open:messaging="openMessaging({ source: ProUnlockSource.PPC })"
            @open:add-to-job-from-resume="openAddToJobFromResume"
          />
        </div>
      </div>

      <div v-show="isLoading" class="results-loading">
        <!-- Gate on isLoading too (not just isNlSearch): the parent uses v-show, so without
             it the loader stays mounted between searches and onMounted never re-runs to
             replay the stage timeline. v-if unmounts it when the search ends. -->
        <StagedSearchLoader v-if="isLoading && isNlSearch && loadingReason === 'query'" />
        <SpinnerLoader v-else class="h-8 w-8" />
      </div>
    </template>
  </div>

  <AddToJobModal
    :open="openMessagingDialog"
    :show-message-field="true"
    :pros="selectedPros"
    :initial-job-id="addToJobInitialJobId"
    :search-session-id="addToJobSearchSessionId"
    @submit="onAddProToJobModalSubmit"
    @close="onAddProToJobModalClose"
    @remove-pro="onAddProToJobModalRemovePro($event)"
  />
</template>

<style scoped lang="postcss">
.results-header {
  @apply sticky top-0 z-10 flex h-[60px] items-center justify-between bg-tint-0 px-4 pb-8 pt-10 sm:px-8;
}
.tab-toggle-container {
  @apply bg-tint-0 px-4 pb-1 sm:px-8;
}
.tab-toggle {
  @apply inline-flex rounded-lg  bg-tint-40 p-1;
}
.tab-toggle-button {
  @apply w-24 rounded-md px-4 py-1.5  text-xs text-shade-820;
}
.tab-toggle-button--active {
  @apply border border-tint-60 bg-tint-0 font-medium text-shade-900 shadow-sm;
}
.candidates-list {
  @apply h-[calc(100vh-128px)] overflow-y-auto px-4 pb-4 pt-3 sm:px-8;
}
.results-main {
  @apply min-h-screen bg-tint-0;
}
.results-loading {
  @apply flex min-h-screen items-center justify-center;
}

/* Legacy path: a transparent wrapper (display:contents) so the shared results/empty
   lay out exactly as before, as direct page children offset by the drawers. */
.search-shell {
  display: contents;
}

/* Smart path: a tint-20 (#F8FAFC) canvas with white cards floating on it, per the
   prototype — not a white surface. The shell is a flex column so the header stays
   pinned and the candidate list scrolls; height comes from smartShellStyle. The
   --smart descendant selectors outrank the base rules above, so the legacy
   viewport-height + white surfaces give way without depending on declaration order. */
.search-shell--smart {
  @apply flex flex-col bg-tint-20;
}
.search-shell--smart .results-main {
  @apply flex min-h-0 flex-1 flex-col bg-tint-20;
}
.search-shell--smart .results-header {
  @apply bg-tint-20;
}
.search-shell--smart .tab-toggle-container {
  @apply bg-tint-20;
}
.search-shell--smart .search-empty {
  @apply h-auto min-h-0 flex-1;
}
.search-shell--smart .results-loading {
  @apply min-h-0 flex-1;
}
.search-shell--smart .candidates-list {
  @apply h-auto min-h-0 flex-1;
}

/* Matches / Skipped read as individual tabs (active = white raised pill), not a
   segmented track. */
.search-shell--smart .tab-toggle {
  @apply gap-1.5 bg-transparent p-0;
}
.search-shell--smart .tab-toggle-button {
  @apply w-auto rounded-lg px-4 py-1.5 text-[13px] font-semibold text-shade-700;
}
.search-shell--smart .tab-toggle-button--active {
  @apply border-transparent bg-tint-0 text-shade-880;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.08),
    0 0 0 1px rgba(15, 23, 42, 0.06);
}

/* Desktop (>= md): the prototype's two columns — full-width header on top, slim filter
   sidebar at left, results at right, separated by the grid gap (no divider line).
   Below md the shell stays the flex column above with the collapsible filters panel.
   The rail grid stays mounted whether or not filters show; the collapsed modifier below
   reclaims the sidebar column so the content can ease back to full width rather than
   snap when the chrome reveals/hides.
   Trade-off: animating grid-template-columns is supported on Chrome 107+ / Firefox 66+ /
   Safari 16+. On Safari/iOS < 16 the column recenters instantly (a clean snap) while the
   chrome still fades — desktop-only, cosmetic, and acceptable for this feature-flagged
   view, so we don't carry a JS width-animation fallback. */
.search-shell--rail {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr);
  column-gap: 24px;
  transition:
    grid-template-columns 180ms ease,
    column-gap 180ms ease;
}
/* No filter chrome yet (bare empty state): collapse the sidebar column to zero so the
   content column spans the full width and the empty state centers across it. Animating
   the column width + gap (in step with the sidebar's fade) eases the content's
   horizontal position so it never jumps when the chrome reveals on keyword/Boolean
   detection or when results arrive. */
.search-shell--rail-collapsed {
  grid-template-columns: 0 minmax(0, 1fr);
  column-gap: 0;
}
.search-shell--rail > header {
  grid-column: 1 / -1;
}
.search-shell--rail > .smart-sidebar {
  @apply min-h-0 overflow-y-auto py-6 pl-8;
  grid-row: 2;
  grid-column: 1;
  /* Grid items default to min-width: auto, which would stop the sidebar shrinking with
     its column; min-width: 0 lets it collapse and overflow-x: hidden clips the card during
     the column ease so it never spills into the centered empty state. */
  min-width: 0;
  overflow-x: hidden;
}
/* RecentSearchesEmpty's root is .empty (not .search-empty); place it explicitly in the
   content column too. Without this it auto-flows into row 2 / col 2 only once the
   sidebar occupies col 1, so its recenter would snap the moment the chrome reveals
   instead of easing with the animated column. */
.search-shell--rail > .empty,
.search-shell--rail > .search-empty,
.search-shell--rail > .results-main,
.search-shell--rail > .results-loading {
  grid-row: 2;
  grid-column: 2;
}
/* Results sit flush to the grid gap on the left; the gap is the only separation. */
.search-shell--rail .results-header,
.search-shell--rail .candidates-list {
  @apply pl-0;
}

/* Graceful reveal for the filter chrome (desktop sidebar / mobile panel): a short fade
   so it eases in when the keyword/Boolean chip activates or results arrive, rather than
   popping. On desktop the horizontal motion comes from the rail's animated sidebar column
   (.search-shell--rail-collapsed → full) growing from zero in step with this fade, so the
   empty state eases back from full-width-centered to the content column; the mobile panel
   just fades — a horizontal slide reads wrong on a block that stacks vertically below the
   search. */
.filter-chrome-enter-active,
.filter-chrome-leave-active {
  transition: opacity 180ms ease;
}
.filter-chrome-enter-from,
.filter-chrome-leave-to {
  opacity: 0;
}

/* Reduced motion: drop the column/gap ease and the fade so the chrome and the recenter
   apply instantly — the layout is correct statically. */
@media (prefers-reduced-motion: reduce) {
  .search-shell--rail,
  .filter-chrome-enter-active,
  .filter-chrome-leave-active {
    transition: none;
  }
}
</style>
